'use client';

import React from 'react';
import Link from 'next/link';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { numberShorthand } from '@benzinga/utils';
import type { GovernmentMember, SecurityTrader } from '@benzinga/gov-trades';

import { memberDisplayNameToProfileSlug } from '../../../../src/components/GovTrades/memberProfileSlug';
import { currentMemberLabelFromStatus, getFullStateName, getPoliticalPartyName } from './utils';

type Variant = 'volume' | 'gainer' | 'loser';

type TopVolumeLeaderCardProps =
  | { variant?: 'volume'; member: GovernmentMember }
  | { variant: 'gainer' | 'loser'; trader: SecurityTrader };

const VARIANT_COPY: Record<Variant, { eyebrow: string; footnote: string }> = {
  volume: {
    eyebrow: 'TOP VOLUME LEADER',
    footnote: 'Highest disclosed trade volume in the tracker period',
  },
  gainer: {
    eyebrow: 'TOP GAINER',
    footnote: 'Best 12-month return performance',
  },
  loser: {
    eyebrow: 'TOP LOSER',
    footnote: 'Worst 12-month return performance',
  },
};

type NormalizedCardData = {
  displayName: string;
  headshot?: string;
  party?: string;
  chamber?: string;
  state?: string;
  status?: string;
  volume?: number;
  totalTrades?: number;
  lastTraded?: string;
  returnPercent?: number;
};

const normalizeFromMember = (member: GovernmentMember): NormalizedCardData => ({
  displayName: member.filer_info?.display_name,
  headshot: member.filer_info?.headshot,
  party: member.filer_info?.party,
  chamber: member.filer_info?.chamber,
  state: member.filer_info?.state,
  status: member.filer_info?.status,
  volume: member.stats?.total_volume_of_trades,
  totalTrades: member.stats?.number_of_trades,
  lastTraded: member.stats?.date_of_last_recorded_trade,
});

const normalizeFromTrader = (trader: SecurityTrader): NormalizedCardData => ({
  displayName: trader.display_name,
  headshot: trader.headshot,
  party: trader.party,
  chamber: trader.chamber,
  state: trader.state,
  volume: trader.ttm_total_volume,
  totalTrades: trader.total_trades,
  returnPercent: trader.ttm_return,
});

const MetricCell = ({
  label,
  children,
  valueClassName,
}: {
  label: string;
  children: React.ReactNode;
  valueClassName?: string;
}) => (
  <div className="flex min-w-0 flex-col gap-0.5">
    <span className="text-xs font-medium text-bzgray-600">{label}</span>
    <span className={clsx('text-sm font-bold leading-[1.15] text-bzgray-900', valueClassName)}>{children}</span>
  </div>
);

const DetailLine = ({
  label,
  children,
  valueClassName,
}: {
  label: string;
  children: React.ReactNode;
  valueClassName?: string;
}) => (
  <div className="mt-0.5 flex items-baseline justify-between gap-3 border-t border-bzblue-300 pt-[0.45rem] text-[13px]">
    <span className="font-normal text-bzgray-600">{label}</span>
    <span className={clsx('font-bold tabular-nums text-bzgray-900', valueClassName)}>{children}</span>
  </div>
);

export const TopVolumeLeaderCard = (props: TopVolumeLeaderCardProps) => {
  const variant: Variant = props.variant ?? 'volume';
  const data: NormalizedCardData =
    'member' in props ? normalizeFromMember(props.member) : normalizeFromTrader(props.trader);

  const slug = memberDisplayNameToProfileSlug(data.displayName);
  const href = `/government-trades/members/${slug}`;

  const subtitle = [getPoliticalPartyName(data.party ?? ''), data.chamber, getFullStateName(data.state ?? '')]
    .map(part => (typeof part === 'string' ? part.trim() : part))
    .filter((part): part is string => Boolean(part) && part !== '-')
    .join(' / ');

  const currentMemberLabel = variant === 'volume' ? currentMemberLabelFromStatus(data.status) : 'yes';
  const { eyebrow, footnote } = VARIANT_COPY[variant];

  const showReturn = variant !== 'volume' && typeof data.returnPercent === 'number';
  const returnPct = (data.returnPercent ?? 0) * 100;
  const returnIsPositive = returnPct >= 0;

  return (
    <div className="relative flex h-full min-h-0 w-full min-w-0 flex-col overflow-hidden rounded-xl border border-bzblue-400 bg-white text-left shadow-none">
      <div className="flex h-full min-h-0 flex-1 flex-col justify-between px-4 py-3.5">
        <h3 className="m-0 text-sm font-bold leading-tight text-bzgray-900">{eyebrow}</h3>

        <Link className="group mt-0.5 flex flex-row items-center gap-3 text-left text-inherit no-underline" href={href}>
          <div className="shrink-0">
            <img
              alt={data.displayName}
              className="block h-[3.25rem] w-[3.25rem] rounded-full border border-bzblue-400 object-cover object-top"
              onError={({ currentTarget }) => {
                currentTarget.onerror = null;
                currentTarget.src = '/next-assets/images/no_picture.png';
              }}
              src={data.headshot}
            />
          </div>
          <div className="min-w-0">
            <p className="mb-0.5 mt-0 text-[clamp(1.05rem,1.5vw,1.25rem)] font-extrabold leading-[1.15] text-black transition-colors duration-150 group-hover:text-bzblue-700">
              {data.displayName}
            </p>
            <p className="m-0 text-[13px] font-normal leading-[1.35] text-bzgray-600 capitalize">{subtitle}</p>
          </div>
        </Link>

        <div className="mt-0.5 grid grid-cols-2 gap-x-3 gap-y-1.5 border-t border-bzblue-300 pt-2" aria-label="Trading activity">
          {variant === 'volume' && (
            <MetricCell label="Trade volume">
              {typeof data.volume === 'number' ? `$${numberShorthand(data.volume, 1)}` : '—'}
            </MetricCell>
          )}
          <MetricCell label="Total trades">
            {typeof data.totalTrades === 'number' ? data.totalTrades.toLocaleString() : '—'}
          </MetricCell>
          <MetricCell label="Chamber">{data.chamber || '—'}</MetricCell>
          {showReturn ? (
            <MetricCell label="Return" valueClassName={returnIsPositive ? 'text-green-700' : 'text-red-700'}>
              {returnIsPositive ? '+' : ''}
              {returnPct.toFixed(2)}%
            </MetricCell>
          ) : (
            <MetricCell label="Last traded">
              {data.lastTraded ? dayjs(data.lastTraded).format('MMM D, YYYY') : '—'}
            </MetricCell>
          )}
        </div>

        <div className="mt-0.5 flex flex-col gap-0">
          <DetailLine label="Current member" valueClassName={clsx('capitalize', currentMemberLabel === 'yes' && 'text-green-700')}>
            {currentMemberLabel}
          </DetailLine>
          <DetailLine label="Party">{getPoliticalPartyName(data.party ?? '')}</DetailLine>
          <DetailLine label="State" valueClassName="capitalize">
            {getFullStateName(data.state ?? '')}
          </DetailLine>
        </div>

        <div className="mt-auto flex flex-col gap-2 border-t border-bzblue-300 pt-[0.45rem]">
          <p className="m-0 text-[11px] font-normal leading-[1.35] text-[#7c8ca5]">{footnote}</p>
          <Link className="block text-center text-sm font-bold uppercase leading-tight text-[#3f83f8] no-underline hover:underline" href={href}>
            View profile
          </Link>
        </div>
      </div>
    </div>
  );
};
